import React, { useEffect, useState } from 'react';
import { Check, AlertTriangle, ArrowRight, TrendingUp, Shield, Zap, IndianRupee } from 'lucide-react';

/**
 * Strategy Report — Standalone, public, shareable page.
 *
 * Route: /strategy-report (no auth required)
 * Audience: Ellora EPC board + senior management
 * Design philosophy: editorial / long-form. Stripe Atlas + Notion docs as
 * reference. High typography contrast, generous whitespace, sticky TOC,
 * numbered modules, consistent KPI cards, soft brand palette.
 */
export default function StrategyReport() {
  const [activeSection, setActiveSection] = useState('exec-summary');

  useEffect(() => {
    document.title = 'ERP Scale-Readiness — Ellora EPC';
    const handler = () => {
      const sections = document.querySelectorAll('section[id]');
      let current = '';
      sections.forEach((s) => {
        if (s.getBoundingClientRect().top <= 160) current = s.id;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafbfc] text-slate-900" style={{ fontFamily: 'Inter, -apple-system, sans-serif' }}>
      <TopBar />
      <CoverBlock />

      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-12 gap-10 pb-32">
        {/* Sticky TOC */}
        <aside className="hidden lg:block col-span-3 pt-12">
          <div className="sticky top-24">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-3">On this page</div>
            <TOC activeSection={activeSection} />
          </div>
        </aside>

        {/* Main column */}
        <main className="col-span-12 lg:col-span-9 pt-12 space-y-24">
          <ExecutiveSummary />
          <WhyERP />
          <ScaleReality />
          <CurrentState />
          <ModuleBD />
          <ModuleHR />
          <ModuleBid />
          <ModuleProject />
          <ModuleEVM />
          <ModuleProcurement />
          <ModuleSubcontractor />
          <ModuleEquipment />
          <ModuleQHSE />
          <ModuleFinance />
          <ModuleDocEng />
          <ModuleAnalytics />
          <Architecture />
          <PhasedPlan />
          <CostSummary />
          <Risks />
          <ClosingCTA />
        </main>
      </div>

      <Footer />
    </div>
  );
}

/* ============================================================
   TOP BAR + COVER
   ============================================================ */
function TopBar() {
  return (
    <header className="sticky top-0 z-30 bg-white/85 backdrop-blur border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/assets/ellora-group-blue.png" alt="Ellora" className="h-7" />
          <span className="hidden sm:inline text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
            ERP Scale-Readiness Report
          </span>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span className="hidden sm:inline">Confidential · Internal Distribution</span>
          <span className="hidden sm:inline">·</span>
          <span>v1.0 · {new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long' })}</span>
        </div>
      </div>
    </header>
  );
}

function CoverBlock() {
  return (
    <section className="relative overflow-hidden bg-[#1c2a4d] text-white">
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: 'radial-gradient(circle at 20% 30%, #5a6896 0%, transparent 50%), radial-gradient(circle at 80% 70%, #3b4872 0%, transparent 50%)',
      }} />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60 mb-6">
          Board · Strategy Document · FY26 — FY30
        </div>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight max-w-4xl" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
          From <span className="text-[#a8b4d8]">₹1,000 Cr</span> to <span className="text-[#a8b4d8]">₹5,000 Cr</span>.<br />
          The ERP roadmap that gets us there.
        </h1>
        <p className="mt-6 text-base lg:text-lg text-white/75 leading-relaxed max-w-3xl">
          A board-level review of where the in-house Ellora EPC ERP stands today,
          what it must deepen to support FY26's ₹1,000 Cr revenue commitment,
          and the 24-month investment plan to reach ₹5,000 Cr by 2030.
        </p>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/15 pt-10">
          <CoverStat label="MVP modules" value="17" sub="production-deployed" />
          <CoverStat label="Target FY26" value="₹1,000 Cr" sub="revenue commitment" />
          <CoverStat label="Target FY30" value="₹5,000 Cr" sub="5× growth" />
          <CoverStat label="24-month ask" value="₹1.6 – 2.2 Cr" sub="phased, value-accretive" />
        </div>
      </div>
    </section>
  );
}

function CoverStat({ label, value, sub }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/55 mb-2">{label}</div>
      <div className="font-serif text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Georgia, serif' }}>{value}</div>
      <div className="text-xs text-white/55 mt-1">{sub}</div>
    </div>
  );
}

/* ============================================================
   STICKY TOC
   ============================================================ */
const TOC_ITEMS = [
  { id: 'exec-summary', label: 'Executive Summary', section: 'Overview' },
  { id: 'why-erp', label: 'Why ERP decides the journey' },
  { id: 'scale-reality', label: 'Operational scale reality' },
  { id: 'current-state', label: "What's been built" },
  { id: 'module-bd', label: 'BD & Pre-Sales', section: 'Modules' },
  { id: 'module-hr', label: 'People & HR' },
  { id: 'module-bid', label: 'Bid & Estimation' },
  { id: 'module-project', label: 'Project Execution' },
  { id: 'module-evm', label: 'Cost Control · EVM' },
  { id: 'module-procurement', label: 'Procurement & Materials' },
  { id: 'module-subcontractor', label: 'Subcontractor' },
  { id: 'module-equipment', label: 'Plant & Equipment' },
  { id: 'module-qhse', label: 'Quality, Health & Safety' },
  { id: 'module-finance', label: 'Finance & Accounting' },
  { id: 'module-doc-eng', label: 'Documents & Engineering' },
  { id: 'module-analytics', label: 'Analytics & BI' },
  { id: 'architecture', label: 'Architecture roadmap', section: 'Roadmap' },
  { id: 'phased-plan', label: '3-year phased plan' },
  { id: 'cost-summary', label: 'Cost summary' },
  { id: 'risks', label: 'Risks & recommendations' },
];

function TOC({ activeSection }) {
  let lastSection = null;
  return (
    <nav className="space-y-0.5 text-[13px]">
      {TOC_ITEMS.map((item) => {
        const showHeader = item.section && item.section !== lastSection;
        lastSection = item.section || lastSection;
        return (
          <React.Fragment key={item.id}>
            {showHeader && (
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 mt-5 mb-2">
                {item.section}
              </div>
            )}
            <a
              href={`#${item.id}`}
              className={`block py-1.5 px-3 -ml-3 rounded-sm transition-colors leading-snug ${
                activeSection === item.id
                  ? 'bg-[#3b4872] text-white font-semibold'
                  : 'text-slate-600 hover:text-[#3b4872] hover:bg-slate-100'
              }`}
            >
              {item.label}
            </a>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

/* ============================================================
   REUSABLE PRIMITIVES
   ============================================================ */
function SectionHeader({ overline, title, lead }) {
  return (
    <div className="mb-8">
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#3b4872] mb-3">{overline}</div>
      <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight text-slate-900 max-w-3xl" style={{ fontFamily: 'Georgia, serif' }}>
        {title}
      </h2>
      {lead && <p className="mt-5 text-base text-slate-600 leading-relaxed max-w-3xl">{lead}</p>}
    </div>
  );
}

function H3({ children }) {
  return <h3 className="font-serif text-xl font-bold text-slate-900 mt-10 mb-3" style={{ fontFamily: 'Georgia, serif' }}>{children}</h3>;
}

function Para({ children }) {
  return <p className="text-[15px] text-slate-700 leading-[1.7] mb-4 max-w-3xl">{children}</p>;
}

function Callout({ kind = 'info', title, children }) {
  const styles = {
    info:    { bg: 'bg-indigo-50',   border: 'border-l-indigo-500', icon: <Zap size={16} className="text-indigo-600" /> },
    success: { bg: 'bg-emerald-50',  border: 'border-l-emerald-500', icon: <Check size={16} className="text-emerald-600" /> },
    warn:    { bg: 'bg-amber-50',    border: 'border-l-amber-500', icon: <AlertTriangle size={16} className="text-amber-600" /> },
    danger:  { bg: 'bg-rose-50',     border: 'border-l-rose-500', icon: <AlertTriangle size={16} className="text-rose-600" /> },
  }[kind];
  return (
    <div className={`${styles.bg} ${styles.border} border-l-4 rounded-r-sm p-5 my-6 max-w-3xl`}>
      {title && (
        <div className="flex items-center gap-2 mb-2">
          {styles.icon}
          <div className="font-bold text-slate-900 text-[13px] uppercase tracking-wider">{title}</div>
        </div>
      )}
      <div className="text-[14px] text-slate-700 leading-[1.7]">{children}</div>
    </div>
  );
}

function DataTable({ headers, rows, highlightLastRow, align }) {
  return (
    <div className="overflow-x-auto -mx-2 my-6 max-w-full">
      <table className="min-w-full text-[13px] border-collapse">
        <thead>
          <tr className="bg-[#1c2a4d] text-white">
            {headers.map((h, i) => (
              <th key={i} className="text-left font-semibold px-4 py-3 first:rounded-tl-sm last:rounded-tr-sm" style={{ textAlign: align?.[i] || 'left' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => {
            const isLast = highlightLastRow && ri === rows.length - 1;
            return (
              <tr key={ri} className={isLast ? 'bg-indigo-50 font-bold' : ri % 2 ? 'bg-slate-50' : 'bg-white'}>
                {r.map((c, ci) => (
                  <td key={ci} className={`px-4 py-3 border-b border-slate-200 text-slate-700 ${isLast ? 'text-[#1c2a4d]' : ''}`} style={{ textAlign: align?.[ci] || 'left' }}>
                    {c}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SubModuleCard({ num, title, problem, solution, effort, cost, priority }) {
  const pColor = { P0: 'bg-rose-100 text-rose-700', P1: 'bg-amber-100 text-amber-700', P2: 'bg-slate-100 text-slate-700' }[priority];
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-6 mb-4 max-w-3xl hover:border-[#3b4872]/40 hover:shadow-sm transition-all">
      <div className="flex items-start gap-3 mb-3">
        <span className="font-serif font-bold text-[#3b4872] text-lg mt-0.5" style={{ fontFamily: 'Georgia, serif' }}>{num}</span>
        <h4 className="font-bold text-slate-900 text-[15px] flex-1 leading-snug">{title}</h4>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${pColor} shrink-0`}>{priority}</span>
      </div>
      <div className="ml-7">
        <div className="text-[13px] text-slate-600 leading-[1.7] mb-3">
          <span className="font-semibold text-slate-900">Problem.</span> {problem}
        </div>
        <div className="text-[13px] text-slate-700 leading-[1.7] mb-4">
          <span className="font-semibold text-slate-900">What we build.</span>
          <ul className="mt-2 space-y-1.5">
            {solution.map((s, i) => (
              <li key={i} className="flex items-start gap-2 pl-1">
                <Check size={13} className="text-emerald-600 mt-1 shrink-0" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex items-center gap-5 text-[11px] pt-3 border-t border-slate-100">
          <Metric label="Effort" value={`${effort} days`} />
          <Metric label="Cost" value={`₹${cost}L`} accent />
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <span className="text-slate-500 uppercase tracking-wider font-semibold mr-1.5">{label}</span>
      <span className={`font-bold ${accent ? 'text-[#3b4872]' : 'text-slate-900'}`}>{value}</span>
    </div>
  );
}

function ModuleSummaryBox({ totalEffort, totalCost, roi }) {
  return (
    <div className="bg-gradient-to-br from-[#1c2a4d] to-[#3b4872] text-white rounded-sm p-6 my-8 max-w-3xl">
      <div className="grid grid-cols-2 gap-6 mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-2">Module Total Effort</div>
          <div className="font-serif text-3xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>{totalEffort} <span className="text-base font-normal text-white/70">days</span></div>
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60 mb-2">Module Total Cost</div>
          <div className="font-serif text-3xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>₹{totalCost}<span className="text-base font-normal text-white/70"> Lakh</span></div>
        </div>
      </div>
      {roi && (
        <div className="text-[13px] text-white/85 leading-relaxed pt-4 border-t border-white/15">
          <span className="font-bold text-white">ROI lens. </span>{roi}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   CONTENT SECTIONS
   ============================================================ */
function ExecutiveSummary() {
  return (
    <section id="exec-summary">
      <SectionHeader
        overline="01 · Executive Summary"
        title="A ₹1.6–2.2 Cr investment over 24 months that conservatively unlocks ₹80–200 Cr of margin, working-capital, and win-rate value."
        lead="Ellora's in-house ERP is already production-deployed and architecturally sound. Reaching ₹1,000 Cr in FY26 requires depth in 4 critical modules. Reaching ₹5,000 Cr by FY30 requires a layered 24-month transformation — no rewrite, just additive growth."
      />
      <H3>Key findings</H3>
      <ul className="space-y-3 text-[14px] text-slate-700 max-w-3xl leading-[1.7]">
        {[
          <><b>BD & Pre-Sales</b> needs PQ Vault, Similar Works Repository, JV management and win-rate analytics to compete for ₹100 Cr+ tenders.</>,
          <><b>HR</b> needs a full ATS, site manpower planner, scaled payroll, biometric integration and BOCW labour compliance — without these, growth from 50 → 600 employees will collapse.</>,
          <><b>Finance</b> is the single largest gap. Project-wise P&L, GST automation, e-invoicing, AR aging, and cash-flow forecasting are mandatory for SEBI-grade audit and board oversight.</>,
          <><b>EVM (Earned Value Management)</b> — the industry-standard cost-control method — is missing. This is the most-asked-for report at board reviews and is critical for projects above ₹50 Cr.</>,
          <>Architecture must evolve from single-instance to high-availability multi-AZ between FY27 and FY28; data-warehouse layer needed by FY29.</>,
        ].map((t, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="font-serif font-bold text-[#3b4872] text-lg" style={{ fontFamily: 'Georgia, serif' }}>{i + 1}.</span>
            <span>{t}</span>
          </li>
        ))}
      </ul>

      <H3>Investment snapshot</H3>
      <DataTable
        headers={['Phase', 'Period', 'Scope', 'Investment (₹ Lakh)']}
        rows={[
          ['Phase 1', 'Next 3 months', 'Critical for FY26 — Finance core, ATS, EVM, BD depth, infra basics', '38 – 52'],
          ['Phase 2', 'Months 4–9', 'Operational maturity — Subcontractor lifecycle, Procurement 3-way match, mobile apps, WhatsApp', '55 – 75'],
          ['Phase 3', 'Months 10–24', 'Scale to ₹5,000 Cr — microservices, data warehouse, multi-entity, ISO/SOC2 certs', '70 – 95'],
          ['Total', '24 months', 'End-to-end transformation', '163 – 222'],
        ]}
        highlightLastRow
        align={[null, null, null, 'right']}
      />
      <Callout kind="success" title="Context">
        ₹1.6–2.2 Cr represents <b>0.16% – 0.22% of FY26 target revenue</b>. Industry benchmark for IT-spend in project-driven businesses is 0.6–1.2% (KPMG, EY 2024). The proposed investment is materially below benchmark while delivering an audit-grade, in-house, vendor-lock-free platform.
      </Callout>
    </section>
  );
}

function WhyERP() {
  return (
    <section id="why-erp">
      <SectionHeader
        overline="02 · The thesis"
        title="Indian EPCs that crossed ₹5,000 Cr share one operational pattern — a mature, internalised ERP."
        lead="L&T, Tata Projects, AFCONS, Dilip Buildcon, KEC, NCC — all run on deeply-integrated ERPs that absorb the complexity of 80-150 concurrent projects. The most common cause of stalled growth between ₹500 Cr and ₹2,000 Cr is operational chaos from ad-hoc spreadsheets."
      />
      <H3>The four levers an ERP pulls</H3>
      <DataTable
        headers={['Lever', 'What it improves', 'Typical impact at ₹1,000 Cr']}
        rows={[
          ['Margin Protection', 'Project-wise P&L, EVM, claim defensibility', '1.5 – 3.0% margin uplift = ₹15 – ₹30 Cr / yr'],
          ['Working Capital', 'AR aging, payment-cycle visibility, e-invoicing', '10 – 20 days DSO reduction = ₹30 – ₹60 Cr freed up'],
          ['Bid Win-Rate', 'Rate library, similar-works repo, weighted pipeline', '2 – 5% win-rate uplift = ₹40 – ₹100 Cr added pipeline'],
          ['Compliance Risk', 'Statutory, BOCW, GST, e-way, ISO certifications', 'Avoidance of ₹1 – ₹10 Cr penalties + audit-pass'],
        ]}
      />
      <Callout kind="success" title="Bottom line">
        A ₹1.6–2.2 Cr investment over 24 months unlocks ₹80–200 Cr of margin + working-capital + win-rate value. ROI payback cycle: 6–12 months.
      </Callout>
    </section>
  );
}

function ScaleReality() {
  return (
    <section id="scale-reality">
      <SectionHeader
        overline="03 · Scale reality check"
        title="At ₹5,000 Cr, the ERP must handle 100× the transaction volume of today."
        lead="Several systems that work fine today break silently as transaction volume crosses inflection points. Sized using industry benchmarks (CRISIL, KPMG EPC 2023, L&T annual reports)."
      />
      <DataTable
        headers={['Operational metric', 'Today', 'FY26 · ₹1,000 Cr', 'FY30 · ₹5,000 Cr']}
        rows={[
          ['Concurrent active projects', '5 – 10', '20 – 30', '80 – 120'],
          ['Average ticket size', '₹20 – 50 Cr', '₹40 – 80 Cr', '₹50 – 150 Cr'],
          ['Total employees (white + blue collar)', '~50', '400 – 600', '2,000 – 3,000'],
          ['Live tenders submitted / month', '5 – 10', '30 – 50', '100 – 150'],
          ['Active vendors + subcontractors', '~50', '500', '2,000'],
          ['Daily DPRs filed across sites', '5 – 10', '20 – 30', '80 – 120'],
          ['RA Bills processed / month', '10', '50 – 80', '250 – 400'],
          ['Inventory transactions / day', '50', '500', '2,000+'],
          ['Daily-active ERP users', '20', '200', '800 – 1,000'],
          ['Peak DB writes / second', '< 10', '50 – 100', '500 – 1,000'],
          ['Statutory filings / month', '~10', '60 – 80', '200 – 300'],
          ['Compliance audits / year', '1 – 2', '6 – 8', '15 – 20'],
        ]}
        align={[null, 'right', 'right', 'right']}
      />
      <Callout kind="warn" title="Implication">
        This is not a 'configure-and-go' jump. It's the difference between a single instance and a multi-region distributed system. Architecture decisions made in Phase 1 determine whether Phase 3 is feasible.
      </Callout>
    </section>
  );
}

function CurrentState() {
  return (
    <section id="current-state">
      <SectionHeader
        overline="04 · Today"
        title="17 modules live in production. Strong MVP foundation. Honest gaps where deepening is required."
        lead="The ERP runs at elloraepc.in on an industry-standard open-source stack (FastAPI · React · MongoDB · JWT · Object Storage). Codebase is owned by Ellora — no vendor lock-in."
      />
      <H3>Strengths</H3>
      <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
        {[
          ['API-first design', 'Every page reads from versioned REST APIs; ready for mobile and external integrations.'],
          ['Department-driven RBAC', '13 departments, 6 role types, granular module access.'],
          ['Modern auth', 'JWT + Bearer, optional Google OAuth, Safari-compatible.'],
          ['Object Storage', 'Already wired for CRM, HR, drawings, RA bill attachments.'],
          ['Audit trail', 'Every user action logged with IP and timestamp.'],
          ['Single source of truth', 'BOQ ↔ WBS ↔ DPR ↔ Stores ↔ RA Bills are linked.'],
        ].map(([title, desc]) => (
          <div key={title} className="border border-slate-200 rounded-sm bg-white p-4">
            <div className="flex items-center gap-2 mb-1">
              <Check size={14} className="text-emerald-600" />
              <div className="font-bold text-[13px] text-slate-900">{title}</div>
            </div>
            <p className="text-[12.5px] text-slate-600 leading-[1.6]">{desc}</p>
          </div>
        ))}
      </div>

      <H3>Honest gaps</H3>
      <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
        {[
          ['Finance module', 'No GL, no project-wise P&L, no GST automation, no e-invoicing.'],
          ['Recruitment / ATS', 'No applicant tracking — blocks scaling hires from 50 → 600.'],
          ['EVM / Cost Control', 'Missing — the headline cost-control report not built.'],
          ['Subcontractor & QHSE', 'Skeletons — workflow depth missing.'],
          ['Single-instance deployment', 'No high-availability, no DR site, no horizontal scaling.'],
          ['No BI / data warehouse', 'Exec dashboards are OLTP-bound; will slow as data grows.'],
          ['No mobile apps', 'Site users currently access via mobile browser only.'],
        ].map(([title, desc]) => (
          <div key={title} className="border border-amber-200 bg-amber-50/40 rounded-sm p-4">
            <div className="flex items-center gap-2 mb-1">
              <AlertTriangle size={14} className="text-amber-600" />
              <div className="font-bold text-[13px] text-slate-900">{title}</div>
            </div>
            <p className="text-[12.5px] text-slate-600 leading-[1.6]">{desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============================================================
   MODULE SECTIONS — using consistent layout
   ============================================================ */
function ModuleBD() {
  return (
    <section id="module-bd">
      <SectionHeader
        overline="Module 01 · Business Development"
        title="BD & Pre-Sales"
        lead="The CRM is one of the strongest parts of the ERP — 9 sub-pages, AI Opportunity Scanner, SLA-driven aging, integrated document storage. Several capabilities essential for ₹100 Cr+ tendering are missing."
      />
      <SubModuleCard num="1.1" priority="P0" effort={8} cost={0.8}
        title="Pre-Qualification (PQ) Document Vault"
        problem="Tenders above ₹50 Cr require a PQ package: turnover certificates, similar-work experience certificates, balance sheets, equipment lists, key personnel CVs, JV deeds. Today these live in random folders. A single expired certificate disqualifies Ellora from a tender — multiple ₹100 Cr+ opportunities can be lost per year for this reason alone."
        solution={[
          'Central PQ artefact repository with versioning and expiry tracking',
          'Auto-alerts 60 / 30 / 7 days before any certificate expiry',
          'Tag-based search (sector, geography, work type, capacity)',
          'Auto-attach to tender submission packets — one-click bundle',
          'Access-controlled by department (BD + Tendering + MD only)',
        ]} />
      <SubModuleCard num="1.2" priority="P0" effort={5} cost={0.5}
        title="Similar-Works Repository"
        problem="PQ submissions demand a structured list of completed similar projects. Today this is compiled manually for every bid — taking 4–8 hours and prone to errors."
        solution={[
          'Structured database of past projects with searchable filters',
          "Filter UI: 'Show all power-plant projects > ₹50 Cr completed in last 5 years'",
          "Auto-generate PDF table in client's PQ format (3–5 common formats)",
          'Performance certificates linked to each project',
        ]} />
      <SubModuleCard num="1.3" priority="P1" effort={4} cost={0.4}
        title="Key Personnel CV Bank"
        problem="PQ submissions require CVs of key personnel in client-mandated format. CV updates are sporadic; outdated versions get submitted."
        solution={[
          'Live, version-controlled CV bank for all senior staff',
          'Auto-pull from HR Employee Master (designation, certifications, experience)',
          'Multiple CV templates (NTPC, NHAI, PWD, Adani, Reliance standard formats)',
          'Mandatory annual refresh workflow with employee acknowledgement',
        ]} />
      <SubModuleCard num="1.4" priority="P0" effort={6} cost={0.6}
        title="Win-Rate & Pipeline Analytics"
        problem="At ₹1,000 Cr revenue, Ellora will pursue ₹6,000–8,000 Cr of tendered opportunities annually. Industry win-rate is 14–18%. Without rigorous analytics, BD chases low-yield leads, wasting ₹3–8 Cr/year in pursuit cost."
        solution={[
          'Win-rate dashboard sliced by sector / client / geography / ticket-size',
          'Pipeline velocity (days Lead → PQ → Bid → Award) with drop-off funnel',
          'Weighted pipeline forecast (pipeline × probability = 12-month revenue forecast)',
          'Competitive intelligence — track who won lost bids and at what value',
          'Pursuit cost ledger tied to opportunities; ROI = won revenue ÷ pursuit cost',
        ]} />
      <SubModuleCard num="1.5" priority="P1" effort={7} cost={0.7}
        title="Joint Venture (JV) & Consortium Management"
        problem="Tenders above ₹500 Cr typically require JV/consortium bidding. JV deeds, scope-split tracking and revenue allocation become complex."
        solution={[
          'JV Master with partner cap-table and shareholding',
          "Scope allocation matrix (e.g. 'Ellora does civil + MEP; Partner X does control systems')",
          'Joint board calendar and minutes archive',
          'Revenue-share auto-calculation linked to RA bills',
          'JV-specific compliance flags (separate PAN, GST, books, audits)',
        ]} />
      <SubModuleCard num="1.6" priority="P1" effort={6} cost={0.6}
        title="Client 360° View"
        problem="At scale, Ellora will work repeatedly with the same clients (NTPC, NHAI, Adani, Reliance, ONGC). A unified 'Client 360' page is critical for relationship management and credit decisions."
        solution={[
          'Aggregate view: past projects, open RA bills, payment behavior (DSO), pending claims, all live opportunities, key contact history',
          'Client credit risk score (auto-computed from payment delays + AR aging)',
          'Relationship-manager assignment with annual review cycle',
          'Auto-alerts on stalled payments or relationship-risk signals',
        ]} />
      <SubModuleCard num="1.7" priority="P1" effort={4} cost={0.4}
        title="BD-to-Operations Handoff (Award Kit)"
        problem="When BD wins a tender, tender intelligence often gets lost in handoff to Projects. This is among the top 3 causes of margin erosion in Indian EPC (KPMG 2023)."
        solution={[
          "Auto-compiled 'Award Kit' PDF travels with project from BD → PM",
          'Original BOQ rates with margin assumptions, productivity norms, client meeting minutes, key promises made during negotiation',
          'Watermarked confidential — restricted to PM + MD',
          'Reference document for first 90 days of execution',
        ]} />
      <ModuleSummaryBox totalEffort={40} totalCost={4.0}
        roi="A 2% win-rate uplift on a ₹6,000 Cr pursuit base = ₹120 Cr additional won revenue. At Ellora's target margin (~8-10%), that's ₹10-12 Cr additional profit per year. The ₹4L investment pays back within 2 weeks of winning the first additional tender." />
    </section>
  );
}

function ModuleHR() {
  return (
    <section id="module-hr">
      <SectionHeader
        overline="Module 02 · People & HR"
        title="People & HR"
        lead="HR is the most feature-complete area of the existing ERP — Employee Master, 4-step compliance onboarding, attendance, document vault, exit/F&F, shifts, leave policies, PMS, letter generation. Growing from 50 → 600 employees in 12 months requires recruitment, scaled payroll, biometric attendance and labour compliance — most needing builds or significant deepening."
      />
      <SubModuleCard num="2.1" priority="P0" effort={14} cost={1.4}
        title="Recruitment / Applicant Tracking System (ATS)"
        problem="To grow from 50 to 600 employees in 12 months, Ellora will hire ~50 people per month. Without an ATS, this becomes operationally impossible and creates compliance gaps (background verification, offer-letter consistency, joining process)."
        solution={[
          'Job Requisition workflow — site/department raises → HR Head approves → posts to portal',
          'Candidate Pipeline (Sourced → Screened → Interviewed → Offered → Joined → Probation Cleared)',
          'Interview scheduling with Google Calendar / Outlook integration',
          'Offer Letter generator using existing CTC Structure module + auto-emailed',
          'Background Verification (BGV) integration with AuthBridge / OnGrid (~₹500/check)',
          'Recruitment funnel analytics — time-to-hire, cost-per-hire, source effectiveness',
          'Candidate database (talent pool) for future requirements',
        ]} />
      <SubModuleCard num="2.2" priority="P0" effort={10} cost={1.0}
        title="Site Manpower Planning"
        problem="Construction is brutally seasonal and site-specific. A single 500 MW project needs 80–120 engineers + 300–500 workers across 2 years, ramping up and down. The static org chart cannot capture this — without dynamic deployment planning, projects will be chronically under-staffed or paying idle resources."
        solution={[
          'Per-project Manpower Budget locked at award stage',
          'Gantt-style Deployment Planner showing who is where, when, with gap/clash flags',
          'Skill-Demand-Supply Matrix — 6-month look-ahead drives recruitment planning',
          'Inter-project transfer workflow (release-from-A → handover → join-B)',
          'Idle-resource report and cost allocation feeds project-wise P&L',
        ]} />
      <SubModuleCard num="2.3" priority="P0" effort={18} cost={1.8}
        title="Payroll at Scale (multi-state, multi-project)"
        problem="Existing payroll is basic. At 600+ employees there will be ESI, PF, PT, TDS, LWF + project-allocated payroll splits + site allowances + retention bonus + ESOPs + Form 16 + TDS 24Q/26Q. Manual computation risks ₹50K–₹5L penalties per violation."
        solution={[
          'Multi-CTC structure variants (grade-wise + location-wise + project-wise overrides)',
          'Project allocation engine — 60% of John\'s payroll hits Project A, 40% Project B',
          'State-aware statutory engine — ESI, PF, PT (state-specific), LWF (state-specific)',
          'Form 16 generation (annual TDS to employees)',
          'Bank advice file generator (ICICI, HDFC, SBI, Axis corporate NEFT formats)',
          'Salary slip portal — employees self-service download',
          'TDS 24Q / 26Q quarterly statutory returns',
          'Full-and-final settlement automation linked to Exit module',
        ]} />
      <SubModuleCard num="2.4" priority="P0" effort={12} cost={1.2}
        title="Time, Attendance & Biometric Hardware Integration"
        problem="Manual attendance roster works at 50 employees. At 600+ across 20 sites it is unsustainable, and disputes over attendance become significant."
        solution={[
          'Biometric device integration — Realtime, eSSL, ZKTeco (most common in India)',
          'Mobile attendance app with geo-fencing (punch only within 500m of site)',
          'Face-recognition kiosks for migrant labour (industry standard)',
          'Overtime auto-calculation per Factories Act / state shop-establishment rules',
          'Attendance reconciliation dashboard — exceptions, anomalies, multi-punch detection',
        ]} />
      <SubModuleCard num="2.5" priority="P0" effort={11} cost={1.1}
        title="Migrant Labour & BOCW Compliance"
        problem="BOCW Act, Inter-State Migrant Workmen Act, Contract Labour Act — heavy compliance. Penalties run ₹50K–₹5L per violation. With 80+ sites by FY30, labour liability becomes a board-level risk."
        solution={[
          'Worker Master separate from Employee Master',
          'Labour Contractor Master with licenses, validity, manpower deployed',
          'PoCC compliance dashboard — % of workers with valid PF/ESI/insurance/PPE training',
          'BOCW Cess auto-computation (1% of contract value, payable to state board)',
          'Statutory labour return generators (Form V, Form XIII, etc.)',
          '24-hour induction tracker (mandatory before site entry)',
          'Form-XIV electronic muster roll',
        ]} />
      <SubModuleCard num="2.6" priority="P1" effort={9} cost={0.9}
        title="Learning Management System (LMS)"
        problem="HSE certifications, BOCW training, ISO 9001/14001/45001 compliance audits require structured learning records. Without LMS, Ellora will fail ISO audits, blocking access to Adani, Reliance, NTPC tier-1 clients."
        solution={[
          'Course library (HSE, BOCW, fire safety, electrical, height work, confined space)',
          'Quizzes and completion certificates',
          'Certification expiry tracker (HSE certs expire annually) with auto-renewal alerts',
          'Mandatory training assignment by role',
          'Compliance report for ISO audits (one-click export)',
        ]} />
      <SubModuleCard num="2.7" priority="P1" effort={8} cost={0.8}
        title="Engagement, Retention & Career Planning"
        problem="Construction industry attrition is brutal: 25–40% annually. At ₹1,000 Cr scale, active retention programs are essential. Replacement cost of a mid-level engineer is 6–9 months of salary."
        solution={[
          'Monthly employee NPS / pulse surveys',
          'Skill-up tracker — training programs completed, certifications',
          'Career path planner with promotion criteria visible to employee',
          'Long-service awards workflow (5-year, 10-year milestones)',
          'Exit interview analytics — top 3 reasons people leave, ranked',
          'Family + insurance master (group mediclaim, GPA, EDLI, claims)',
        ]} />
      <SubModuleCard num="2.8" priority="P1" effort={6} cost={0.6}
        title="Performance + Compensation Closed Loop"
        problem="Existing PMS captures appraisals, but the loop from rating → increment → letter → payroll update is manual. At scale, this loop must be fully automated for 600+ annual cycles in a 4-week window."
        solution={[
          'Compensation Cycle workflow — bulk increment application by grade',
          'Auto-generate increment letters using existing Letters module',
          'Auto-update CTC effective from cycle date',
          'Bell-curve normalization tool for HR Head',
          'ESOP / LTI grant tracker (mandatory for SEBI compliance at IPO stage)',
        ]} />
      <ModuleSummaryBox totalEffort={88} totalCost={8.8}
        roi="5% reduction in attrition (industry achievable with structured retention) at 600 employees avoids ~₹2-3 Cr in replacement/training costs. Payroll automation avoids 1-2 FTEs of manual processing = ₹15-20L/yr. BOCW automation avoids penalty exposure of ₹1-5 Cr per audit cycle. Total HR module payback: 8-12 months." />
    </section>
  );
}

/* ============================================================
   COMPACT MODULE LAYOUT — for remaining 10 modules
   ============================================================ */
function CompactModule({ id, num, title, lead, intro, items, totalEffort, totalCost, roi }) {
  return (
    <section id={id}>
      <SectionHeader overline={`Module ${num}`} title={title} lead={lead} />
      <Para>{intro}</Para>
      <div className="space-y-3 max-w-3xl">
        {items.map((it) => (
          <div key={it.title} className="border border-slate-200 bg-white rounded-sm p-5 hover:border-[#3b4872]/40 transition-colors">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex items-baseline gap-2">
                <span className="font-serif font-bold text-[#3b4872]" style={{ fontFamily: 'Georgia, serif' }}>{it.num}</span>
                <h4 className="font-bold text-slate-900 text-[14.5px] leading-snug">{it.title}</h4>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] text-slate-500"><b className="text-slate-900">{it.effort}d</b></span>
                <span className="text-[11px] text-[#3b4872] font-bold">₹{it.cost}L</span>
                <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                  it.priority === 'P0' ? 'bg-rose-100 text-rose-700' :
                  it.priority === 'P1' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'
                }`}>{it.priority}</span>
              </div>
            </div>
            <p className="text-[13px] text-slate-700 leading-[1.7]">{it.desc}</p>
          </div>
        ))}
      </div>
      <ModuleSummaryBox totalEffort={totalEffort} totalCost={totalCost} roi={roi} />
    </section>
  );
}

function ModuleBid() {
  return (
    <CompactModule id="module-bid" num="03" title="Bid & Estimation"
      lead="Where revenue starts and margin is locked in. Current Tender Register and Rate Library are foundational only — they need depth to compete at ₹100 Cr+ ticket sizes."
      intro="Bid quality directly drives win-rate (revenue) and margin protection. The primary leak point in current EPC bidding is under-pricing risk and over-trusting verbal productivity assumptions. Structured estimation eliminates both."
      items={[
        { num: '3.1', title: 'Enhanced Rate Library with Regional Indices', desc: 'NCCBM, CPWD DSR and state PWD rate auto-updates. Regional cost-index variation (labour 30% cheaper in UP vs Maharashtra). Quarterly refresh. Historical trends for negotiation leverage.', effort: 6, cost: 0.6, priority: 'P0' },
        { num: '3.2', title: 'Productivity Norms Library', desc: "Ellora's secret sauce — years of 'we did this slab pour in 8 hrs/100m³' data. Per-activity norms with confidence levels (observation count) and trend over time. Re-used on every bid.", effort: 5, cost: 0.5, priority: 'P0' },
        { num: '3.3', title: 'Subcontractor Rate Bank', desc: "When estimating, instantly see 'last time we got this scope at ₹X/m² from Vendor Y in Maharashtra'. Auto-populated from completed POs and Work Orders. Filter by geography, scope, vendor rating.", effort: 4, cost: 0.4, priority: 'P0' },
        { num: '3.4', title: 'Risk-Adjusted Bidding + Margin Simulator', desc: 'Auto-add 2-5% for site risks (weather, terrain, client payment history, distance from material source). Risk register per bid. Margin simulator — "if I drop bid 5%, can I still recover?"', effort: 7, cost: 0.7, priority: 'P0' },
        { num: '3.5', title: 'Bid / No-Bid Decision Matrix', desc: 'Automated scoring against 10 criteria (client credit-worthiness, scope fit, geographic fit, competition intensity). Avoids wasted pursuit on low-yield tenders. Integration with AI Opportunity Scanner.', effort: 5, cost: 0.5, priority: 'P1' },
        { num: '3.6', title: 'Tender Submission Packaging', desc: 'One-click bundle generation — BOQ + PQ documents + similar works + CVs + JV deeds + financials + EMD into client-format PDF/ZIP. NTPC/NHAI/PWD/Adani templates. Pre-submission compliance checklist.', effort: 6, cost: 0.6, priority: 'P0' },
        { num: '3.7', title: 'Post-Bid Analytics + Win-Loss Diary', desc: 'Every closed tender feeds insights back. Loss reason categorisation (rate, technical, relationship, schedule, qualification). Competitor bid value capture where available. Quarterly win-loss review report.', effort: 4, cost: 0.4, priority: 'P1' },
      ]}
      totalEffort={37} totalCost={3.7}
      roi="1% margin protection on ₹1,000 Cr won revenue = ₹10 Cr. The investment pays back 200× in a single year." />
  );
}

function ModuleProject() {
  return (
    <CompactModule id="module-project" num="04" title="Project Execution"
      lead="Functionally the largest area of the existing ERP. Depth additions needed for projects above ₹50 Cr where client-engineers demand Primavera-grade scheduling and structured QA/QC."
      intro="Projects, WBS, BOQ, DPR, MSP / Tasks, Drawings and RA Bills are all live. The deepening below adds the artefacts client-engineers and ISO auditors mandate at higher project values."
      items={[
        { num: '4.1', title: 'Master Construction Schedule + Primavera/MSP Integration', desc: 'Owner-engineers at ₹50 Cr+ projects (NTPC, NHAI, Adani) mandate Primavera P6 schedule submissions. P6 XML round-trip + MSP XML import + critical-path visualisation.', effort: 10, cost: 1.0, priority: 'P1' },
        { num: '4.2', title: '4D BIM Integration', desc: 'Visual progress tracking by overlaying schedule on 3D BIM model. IFC file viewer in browser. Take-off automation from BIM to BOQ. Clients see "wall masonry should be 60% complete by end of June".', effort: 12, cost: 1.2, priority: 'P2' },
        { num: '4.3', title: 'Milestone-Based Progress + RA Bill Trigger', desc: "Currently progress is % only. Move to milestones ('Foundation Complete', 'Roof Cast', 'MEP Roughed-In') that auto-trigger RA bill claim eligibility. Client approval workflow.", effort: 6, cost: 0.6, priority: 'P0' },
        { num: '4.4', title: 'Enhanced DPR (mobile-first + media)', desc: 'Native mobile app (Android-first). GPS-tagged photo upload (geo-fence verified). Drone feed integration (DJI Terra exports). Weather auto-capture from API.', effort: 10, cost: 1.0, priority: 'P0' },
        { num: '4.5', title: 'Risk & Issue Register', desc: 'Per-project register tracking risks (probability × impact = exposure ₹). Mitigation plans, owners, dates. Risk heat-map. Escalation workflow. Tied to claims module for documented basis.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '4.6', title: 'Snag / Punch List with Mobile QA/QC App', desc: 'Mobile-first snag capture by QA/QC engineers walking the site. Photos, location, severity, contractor responsible. Auto-assign + close-out workflow with photo evidence.', effort: 8, cost: 0.8, priority: 'P1' },
        { num: '4.7', title: 'Method Statements + JSA Library', desc: 'Mandatory for client/EPC handover and ISO 45001 audits. ~80 standard method statements, editable per-project, with client approval tracking.', effort: 4, cost: 0.4, priority: 'P1' },
        { num: '4.8', title: 'As-Built Drawing Register', desc: 'End-of-project handover requires as-built drawings. Version-controlled register linked to project closure workflow. Auto-collation and handover package generator.', effort: 5, cost: 0.5, priority: 'P1' },
      ]}
      totalEffort={60} totalCost={6.0} />
  );
}

function ModuleEVM() {
  return (
    <CompactModule id="module-evm" num="05" title="Cost Control · Earned Value Management"
      lead="The industry-standard methodology to quantify 'are we over budget?' and 'are we behind schedule?'. Currently MISSING from the ERP. Mandatory at client review meetings for projects above ₹50 Cr."
      intro="EVM converts data Ellora is already capturing (BOQ + DPR + RA bills) into the most actionable single dashboard in EPC. Early detection of 1 CPI deterioration cycle (caught 6 weeks earlier than reactive review) saves ₹2-5 Cr per ₹100 Cr project."
      items={[
        { num: '5.1', title: 'Snapshot Engine', desc: 'Weekly auto-computation of PV (Planned Value), EV (Earned Value), AC (Actual Cost). Idempotent. Per-project, per-WBS-package. Historical trend captured for S-curve.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '5.2', title: 'KPI Strip (8 metrics)', desc: 'CV, SV, CPI, SPI, EAC, ETC, VAC, TCPI — 12-week sparkline per metric. Traffic-light coloring. Headline verdict: "On budget, 11 days behind schedule. EAC ₹4.82 Cr vs BAC ₹5.10 Cr."', effort: 3, cost: 0.3, priority: 'P0' },
        { num: '5.3', title: 'S-Curve Visualisation', desc: 'Three lines: PV (plan), EV (actual progress), AC (actual cost). The most powerful single chart in construction. Shaded variance regions. Export to PDF for client reviews.', effort: 3, cost: 0.3, priority: 'P0' },
        { num: '5.4', title: 'Three-Method EAC Forecasting', desc: 'Optimistic [AC + (BAC – EV)], Statistical [BAC / CPI], Pessimistic [AC + (BAC – EV) / (CPI × SPI)] side-by-side. PM picks confidence level.', effort: 2, cost: 0.2, priority: 'P0' },
        { num: '5.5', title: 'Portfolio Heatmap', desc: 'Executive view — all projects sorted by CPI ascending. Red projects float to top. Daily scan for MD. One-click drill-down per project.', effort: 3, cost: 0.3, priority: 'P0' },
        { num: '5.6', title: 'Baseline + Re-Baseline Workflow', desc: 'Original baseline locked at project award; re-baseline only on approved variation orders. Audit-grade lock on original BAC. Variation increments stored separately.', effort: 3, cost: 0.3, priority: 'P0' },
        { num: '5.7', title: 'DPR Freshness Indicator', desc: 'EVM accuracy depends on DPR freshness. Surface "Last DPR: 4 days ago — EV may understate progress" on every page. Auto-nudge to PM if stale > 2 days.', effort: 2, cost: 0.2, priority: 'P0' },
      ]}
      totalEffort={21} totalCost={2.1}
      roi="At 20 active projects in FY26, ₹40-100 Cr opportunity from early issue detection — against a ₹2.1L investment." />
  );
}

function ModuleProcurement() {
  return (
    <CompactModule id="module-procurement" num="06" title="Procurement & Materials"
      lead="Where most margin leakage happens in EPC. Foundational modules exist; depth needed in matching, e-auctions, vendor scoring and multi-location inventory."
      intro="At ₹1,000 Cr scale, 3-5% procurement savings on ₹400-600 Cr annual material spend = ₹12-30 Cr/year recurring. Payback < 3 weeks of first e-auction."
      items={[
        { num: '6.1', title: '3-Way Matching (PO ↔ GRN ↔ Invoice)', desc: 'Auto-match PO, GRN and supplier invoice. Block payment on mismatches > ₹10K or 2% variance. Industry-standard control.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '6.2', title: 'e-Auction / Reverse Auction Module', desc: 'Multi-vendor live bidding. Pre-qualified vendor pool. Bid history audit-trail. Auto-award workflow. 3-5% savings standard.', effort: 12, cost: 1.2, priority: 'P1' },
        { num: '6.3', title: 'Vendor Scoring & Approved Vendor List', desc: 'On-time-delivery %, quality reject %, payment-terms-compliance, financial health score. Annual vendor review workflow. AVL gating for new POs.', effort: 6, cost: 0.6, priority: 'P0' },
        { num: '6.4', title: 'Multi-Location Inventory', desc: 'Central stores + site stores + workshop stores with stock transfer workflow. Per-location stock view. Goods-in-transit accounting. Reorder-level alerts.', effort: 8, cost: 0.8, priority: 'P0' },
        { num: '6.5', title: 'Material Master with Substitutes', desc: 'When primary material unavailable, system suggests pre-approved substitutes (e.g. rebar Fe-500 → Fe-550). Rate variance auto-computed. Quality engineer approval gate.', effort: 4, cost: 0.4, priority: 'P1' },
        { num: '6.6', title: 'Enhanced MRP with Lead-Time & Safety-Stock', desc: 'Current MRP is basic. Add vendor lead-time history, ABC class auto-assignment, safety stock per material/location.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '6.7', title: 'Equipment Hire vs Buy Analytics', desc: 'TCO calculator per equipment. Utilization-based break-even analysis. Auto-recommendation. At scale this decision saves crores.', effort: 4, cost: 0.4, priority: 'P2' },
      ]}
      totalEffort={44} totalCost={4.4} />
  );
}

function ModuleSubcontractor() {
  return (
    <CompactModule id="module-subcontractor" num="07" title="Subcontractor Management"
      lead="Currently a skeleton. At ₹1,000 Cr scale, 40-60% of scope is sub-contracted; without proper lifecycle management, margin leakage, disputes and project delays become routine."
      intro="The capabilities below close the SC lifecycle from pre-qualification through retention release, with performance scoring and back-charge automation."
      items={[
        { num: '7.1', title: 'Subcontractor Master + Pre-Qualification', desc: 'Full SC master with bank details, licenses, expertise, capacity, financial health, past performance. KYC workflow. Annual re-qualification. Black-list management.', effort: 6, cost: 0.6, priority: 'P0' },
        { num: '7.2', title: 'Work Order (WO) Workflow', desc: 'Equivalent of PO for services. Scope, rates, schedule, payment milestones, retention terms. WO templates per scope. Approval matrix tied to value. Variation order workflow.', effort: 8, cost: 0.8, priority: 'P0' },
        { num: '7.3', title: 'SC RA Bills (reverse direction)', desc: 'Mirror of client RA bills — SC raises bill to Ellora, Ellora measures and approves. Joint measurement workflow. Quality hold-back deductions. Retention release on warranty expiry.', effort: 7, cost: 0.7, priority: 'P0' },
        { num: '7.4', title: 'SC PMC (Performance Monitoring & Control)', desc: 'Quality, schedule adherence, safety, payment compliance — scored monthly from snags, safety incidents, schedule slippage. Action plan for low performers.', effort: 5, cost: 0.5, priority: 'P1' },
        { num: '7.5', title: 'Retention & Security Deposit Tracker', desc: 'Multi-year retention amounts (typically 5-10% retained for 12-18 months post-completion). Auto-track per SC, per project. Warranty period workflow. Auto-release with compliance checks.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '7.6', title: 'Back-charge Management', desc: 'When SC work needs rectification (paid by Ellora and recovered), or Ellora supplies materials to SC. Back-charge ledger per SC. Auto-deduction from next RA bill. Dispute workflow.', effort: 4, cost: 0.4, priority: 'P1' },
      ]}
      totalEffort={35} totalCost={3.5} />
  );
}

function ModuleEquipment() {
  return (
    <CompactModule id="module-equipment" num="08" title="Plant, Equipment & Logistics"
      lead="High-value, often under-managed area. At ₹1,000 Cr scale, Ellora will own/hire ₹100-200 Cr worth of equipment. Utilization improvements of 10-15% translate to ₹10-30 Cr cost savings."
      intro="The deepening below builds the asset-register-to-fuel-tracker spine that converts equipment from a cost-leak to a managed asset class."
      items={[
        { num: '8.1', title: 'Equipment Master with Depreciation Schedule', desc: 'Asset register integrated with Finance. IT Act + Companies Act dual depreciation. Insurance renewal tracker. Disposal / sale workflow.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '8.2', title: 'Hire-In / Hire-Out Workflow', desc: 'Inter-site equipment movement (Ellora ↔ Ellora) and external hire. Hire rate master. Daily/monthly invoice generation. Idle vs deployed tracker.', effort: 6, cost: 0.6, priority: 'P0' },
        { num: '8.3', title: 'Preventive Maintenance Scheduler', desc: 'Hours-based or KM-based PM schedule. Saves ₹crores in breakdowns. Auto-alerts. Spares consumption history. Cost of ownership per equipment.', effort: 6, cost: 0.6, priority: 'P0' },
        { num: '8.4', title: 'Utilization Analytics', desc: '"This crane was idle 40% of last month — should we sell/transfer?" Daily utilization log. Idle reason categorisation. MD daily report.', effort: 4, cost: 0.4, priority: 'P1' },
        { num: '8.5', title: 'Fuel & Lubricant Tracking', desc: 'Major leakage point. Issue slip workflow. Consumption-per-hour computation. Variance alert on outliers.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '8.6', title: 'Calibration Register', desc: 'Testing equipment calibration tracking — mandatory for ISO 9001. Calibration schedule per instrument. Vendor-managed calibration. Audit-ready report.', effort: 3, cost: 0.3, priority: 'P1' },
      ]}
      totalEffort={29} totalCost={2.9} />
  );
}

function ModuleQHSE() {
  return (
    <CompactModule id="module-qhse" num="09" title="Quality, Health, Safety & Environment"
      lead="Foundation laid (NCR, Snags, Incidents, Toolbox Talks, Permits, Inspections) but workflow depth missing. Increasingly a client mandate (Adani, Reliance, NTPC); ISO 9001/14001/45001 gates access to top-tier clients."
      intro="The QHSE module is where Ellora wins or loses access to the largest tier-1 clients. ISO certification readiness alone justifies the investment."
      items={[
        { num: '9.1', title: 'NCR Workflow + Root-Cause Library', desc: 'Structured NCR with 5-Why, Fishbone analysis. Library of past root causes for trending. NCR lifecycle workflow. Trend report by cause category.', effort: 6, cost: 0.6, priority: 'P0' },
        { num: '9.2', title: 'Incident Management + TRIR / LTIFR', desc: 'Incident reporting, investigation, corrective action. Auto-compute Total Recordable Incident Rate and Lost Time Injury Frequency Rate (mandatory client KPIs). Severity categorisation.', effort: 7, cost: 0.7, priority: 'P0' },
        { num: '9.3', title: 'Permit-to-Work System', desc: 'Hot Work, Confined Space, Working at Height, Excavation, Electrical Isolation. Mandatory for ISO 45001. Permit templates. Approval matrix. Validity expiry alerts.', effort: 8, cost: 0.8, priority: 'P0' },
        { num: '9.4', title: 'Toolbox Talk + Daily Briefing', desc: 'Daily site briefings with attendance + topic + photo. Mandatory regulatory record. Topic library (100+ standard talks). Mobile-first capture. Auto-compliance report.', effort: 4, cost: 0.4, priority: 'P0' },
        { num: '9.5', title: 'Safety KPI Dashboard', desc: 'Leading indicators (near-misses, unsafe acts) + lagging indicators (LTI, fatalities). Required for ISO 45001 management review. Real-time KPI strip. Trending charts. Site-comparison heatmap.', effort: 5, cost: 0.5, priority: 'P1' },
        { num: '9.6', title: 'Environmental Compliance', desc: 'Consent to Operate, hazardous waste manifest, water/electricity meter logs. CTE/CTO expiry tracker. Hazardous waste tracker. Compliance calendar.', effort: 5, cost: 0.5, priority: 'P1' },
      ]}
      totalEffort={35} totalCost={3.5} />
  );
}

function ModuleFinance() {
  return (
    <section id="module-finance">
      <SectionHeader
        overline="Module 10 · Finance"
        title="Finance, Accounting & Statutory"
        lead="The single largest gap. A ₹1,000 Cr enterprise cannot operate without project-wise P&L, GST automation, e-invoicing and integrated banking."
      />
      <Callout kind="danger" title="Critical Priority">
        Every sub-module below is <b>P0</b>. Skipping any of these creates statutory, audit or operational exposure that compounds rapidly at ₹1,000 Cr+ scale.
      </Callout>
      <div className="space-y-3 max-w-3xl">
        {[
          { num: '10.1', title: 'General Ledger + Chart of Accounts', desc: 'Full double-entry GL with Indian Companies Act 2013 Schedule III chart of accounts. Account hierarchy. Multi-cost-centre (project, department, site). Journal entry workflow with approval.', effort: 12, cost: 1.2 },
          { num: '10.2', title: 'Trial Balance, P&L, Balance Sheet', desc: 'Statutory financial statements with Schedule III formatting. Audit-ready. Real-time TB. Comparative P&L. Schedule III BS + notes.', effort: 8, cost: 0.8 },
          { num: '10.3', title: 'Project-wise P&L (CRITICAL)', desc: 'Real-time project profitability. Each invoice, salary, material spend tagged to project. Allows MD to know each project\'s margin daily. Auto-allocation rules. Variance vs original budget.', effort: 10, cost: 1.0 },
          { num: '10.4', title: 'GST Automation (GSTR-1, 3B, 9 + e-invoicing)', desc: 'Mandatory for ₹5 Cr+ turnover. GST registration master (multi-state). Auto-GSTR-1 and GSTR-3B. e-Invoice integration with IRP. Input tax credit reconciliation.', effort: 14, cost: 1.4 },
          { num: '10.5', title: 'TDS / TCS Engine + 26AS Reconciliation', desc: 'Auto-deduct TDS on payments, generate 24Q/26Q returns. Reconcile with 26AS to avoid mismatches. Section-wise TDS computation.', effort: 8, cost: 0.8 },
          { num: '10.6', title: 'e-Way Bill Generation', desc: 'Mandatory for material movements > ₹50K. Auto-generate and link to PO/GRN. Validity extension workflow. Movement tracking.', effort: 5, cost: 0.5 },
          { num: '10.7', title: 'AR Aging by Client', desc: 'EPC clients pay 60-120 DPO. AR management = cashflow lifeblood. Aging buckets (0-30, 31-60, 61-90, 91-180, > 180). Reminder workflow. Client credit-score auto-update.', effort: 6, cost: 0.6 },
          { num: '10.8', title: 'Bank Reconciliation', desc: 'Upload bank statement (MT940/CSV from ICICI, HDFC, SBI, Axis), auto-match with ERP entries. Auto-match by amount + date + UTR. Exception workflow. Daily reco discipline.', effort: 6, cost: 0.6 },
          { num: '10.9', title: 'Cash Flow Forecasting', desc: 'At ₹5,000 Cr, working capital cycle = ₹500 Cr+. 12-week rolling forecast. AR + AP + payroll + capex + tax outflow model. What-if scenarios. MD daily snapshot.', effort: 8, cost: 0.8 },
          { num: '10.10', title: 'Statutory Returns Calendar', desc: 'GST, TDS, PF, ESI, PT, LWF, ROC, IT — 60-80 returns/month at FY26 scale. Single missed return = penalty. Master calendar with auto-reminders. Filing log with proof.', effort: 4, cost: 0.4 },
        ].map((it) => (
          <div key={it.num} className="border border-rose-200 bg-white rounded-sm p-5 hover:border-rose-400 transition-colors">
            <div className="flex items-start justify-between gap-4 mb-2">
              <div className="flex items-baseline gap-2">
                <span className="font-serif font-bold text-rose-600" style={{ fontFamily: 'Georgia, serif' }}>{it.num}</span>
                <h4 className="font-bold text-slate-900 text-[14.5px] leading-snug">{it.title}</h4>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[11px] text-slate-500"><b className="text-slate-900">{it.effort}d</b></span>
                <span className="text-[11px] text-[#3b4872] font-bold">₹{it.cost}L</span>
                <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-700">P0</span>
              </div>
            </div>
            <p className="text-[13px] text-slate-700 leading-[1.7]">{it.desc}</p>
          </div>
        ))}
      </div>
      <ModuleSummaryBox totalEffort={81} totalCost={8.1}
        roi="Project-wise P&L alone delivers 1-2% margin protection = ₹10-20 Cr/yr. GST + e-invoicing automation avoids ₹2-10 Cr penalty exposure annually. Cash flow forecasting saves 5-10 days working capital = ₹15-30 Cr freed capital. Combined Finance ROI alone exceeds all other modules." />
    </section>
  );
}

function ModuleDocEng() {
  return (
    <CompactModule id="module-doc-eng" num="11" title="Document Management & Engineering"
      lead="Engineering documents (CAD, BIM, drawings, RFIs, submittals) are in a basic Files module today. At scale, these need version-controlled engineering DMS with approval workflows."
      intro="Document-controlled processes are an ISO 9001 hard requirement. The capabilities below also reduce schedule risk by ensuring RFIs and submittals don't slip."
      items={[
        { num: '11.1', title: 'CAD / BIM File Storage with Version Control', desc: 'Full version control for AutoCAD DWG, Revit RVT, IFC, Navisworks NWD. Major + minor version numbering. Delta diff between versions. Linked-file management (XREFs).', effort: 10, cost: 1.0, priority: 'P1' },
        { num: '11.2', title: 'Drawing Approval Workflow', desc: 'Internal Review → Submitted to Client → Approved for Construction (AFC). Multi-stage approval matrix. Markup overlay. Client portal for review/approval.', effort: 6, cost: 0.6, priority: 'P0' },
        { num: '11.3', title: 'Submittal Register', desc: 'Material samples, method statements, mock-ups — tracked by client approval status. Submittal lifecycle. Schedule integration. Critical-submittal alerts.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '11.4', title: 'RFI Tracker', desc: 'Clients respond in 7-14 days; tracking prevents schedule slips. Each RFI tied to claim documentation. RFI ID with auto-numbering. Response SLA tracking.', effort: 5, cost: 0.5, priority: 'P0' },
        { num: '11.5', title: 'Transmittal Log', desc: 'Every document sent to client logged with acknowledgement. Critical for dispute defence. Auto-transmittal generator. Receipt tracking. Audit-grade log.', effort: 4, cost: 0.4, priority: 'P1' },
        { num: '11.6', title: 'As-Built Compilation', desc: 'Final handover package auto-compiled from project documents. Closure workflow. Multi-volume PDF compilation. Client signoff tracking.', effort: 4, cost: 0.4, priority: 'P1' },
      ]}
      totalEffort={34} totalCost={3.4} />
  );
}

function ModuleAnalytics() {
  return (
    <CompactModule id="module-analytics" num="12" title="Analytics, BI & Executive Dashboards"
      lead="Existing Executive Dashboard reads directly from MongoDB (OLTP). At ₹5,000 Cr scale, complex analytics need a separate BI layer to avoid degrading operational app performance."
      intro="The layer below transforms the ERP from an operational system into a decision-support platform. The Daily Digest alone is a strong board engagement tool."
      items={[
        { num: '12.1', title: 'Data Warehouse Layer', desc: 'ETL nightly from MongoDB OLTP to ClickHouse / Postgres analytics DB. Pre-aggregated for fast queries. Star-schema for projects, finance, HR. Incremental ETL. Query latency < 2s for 5-year data.', effort: 15, cost: 1.5, priority: 'P1' },
        { num: '12.2', title: 'BI Dashboard Layer', desc: 'Metabase / Apache Superset embedded for ad-hoc analytics by department heads. Self-service BI. Saved dashboards. Scheduled email reports.', effort: 8, cost: 0.8, priority: 'P1' },
        { num: '12.3', title: 'Executive Daily Digest (email)', desc: "MD's daily inbox at 7 AM — overnight KPI summary, alerts, action items. Customizable per role. Mobile-friendly HTML email. WhatsApp/Telegram backup channel.", effort: 4, cost: 0.4, priority: 'P0' },
        { num: '12.4', title: 'Predictive Models (Phase 3)', desc: 'ML models for cost-overrun prediction (6-month early warning), claim auto-generation, vendor fraud detection. Anomaly detection on PO/RA bill values. Tender win-probability scoring.', effort: 20, cost: 2.0, priority: 'P2' },
        { num: '12.5', title: 'Mobile Executive App', desc: 'MD + senior management view-only mobile app — projects, P&L, cash, KPIs. Native iOS + Android. Biometric login. Offline cache for travel.', effort: 15, cost: 1.5, priority: 'P1' },
      ]}
      totalEffort={62} totalCost={6.2} />
  );
}

/* ============================================================
   ARCHITECTURE + PHASED PLAN
   ============================================================ */
function Architecture() {
  return (
    <section id="architecture">
      <SectionHeader
        overline="13 · Platform Architecture"
        title="Scaling the platform itself — Today → FY26 → FY30"
        lead="Module-level depth is necessary but not sufficient. As transaction volume grows 100×, the underlying architecture must evolve. Decisions made in Phase 1 determine whether Phase 3 is feasible."
      />
      <DataTable
        headers={['Layer', 'Today (MVP)', 'FY26 · ₹1,000 Cr', 'FY30 · ₹5,000 Cr']}
        rows={[
          ['Database', 'MongoDB single instance', 'Replica set + read replicas + nightly backups + PITR', 'Sharded Mongo + Postgres + ClickHouse for analytics'],
          ['Backend', 'Single FastAPI process', '2-3 instances behind load balancer', 'Microservices (Auth, Finance, HR, Projects) + RabbitMQ/Kafka'],
          ['Frontend', 'React SPA', 'Same + CDN (Cloudflare) for static assets', 'Next.js SSR + module federation; PWA for offline'],
          ['File Storage', 'Object Storage (Emergent)', '+ Virus scanning + DLP rules', '+ Geo-replication + CDN + DRM for sensitive'],
          ['Backups', 'Daily automated', 'Daily + weekly + monthly + DR site', 'PITR + monthly DR drill + cross-region replication'],
          ['Monitoring', 'App logs', '+ APM (Datadog or Sentry) + uptime + alerting', '+ Distributed tracing + SLO/SLA dashboards + on-call'],
          ['Security', 'JWT + RBAC + bcrypt', '+ Annual pen test + WAF + DDoS + MFA', 'SOC 2 Type 2 + ISO 27001 + ZTNA + DLP + SIEM'],
          ['Auth', 'JWT + Google OAuth', '+ SSO via Azure AD / Okta', 'Full IdP with SAML/OIDC + SCIM user provisioning'],
          ['DevOps', 'Single env', 'Multi-env (dev/UAT/staging/prod) + CI/CD', 'GitOps + Terraform IaC + auto-scaling + canary deploys'],
          ['Compliance', 'Audit log only', '+ ISO 9001 + ISO 27001 readiness', '+ SOC 2 Type 2 + GDPR/DPDP Act'],
        ]}
      />
    </section>
  );
}

function PhasedPlan() {
  return (
    <section id="phased-plan">
      <SectionHeader
        overline="14 · The plan"
        title="3-year phased rollout"
        lead="Investment structured in three phases. Each phase is independently value-accretive — you can pause after any phase. Sequencing prioritises modules that unlock the most operational risk reduction and revenue/margin uplift."
      />

      <H3>Phase 1 — Critical for FY26 ₹1,000 Cr (Next 3 months)</H3>
      <DataTable
        headers={['Workstream', 'Scope', 'Effort (days)', '₹ Lakh']}
        rows={[
          ['Finance Foundation', 'GL + COA, TB/P&L/BS, Project P&L, GST + e-Invoicing, TDS, e-Way, AR Aging, Bank Reco, Cash Flow Forecast, Statutory Calendar', '81', '8.1'],
          ['HR Critical', 'ATS, Site Manpower Planner, Payroll Scale, Attendance + Biometric, BOCW Labour Compliance', '65', '6.5'],
          ['BD Critical', 'PQ Vault, Similar Works Repo, Win-Rate Analytics, Tender Submission Packaging', '25', '2.5'],
          ['Bid & Estimation', 'Rate Library Enhancement, Productivity Norms, SC Rate Bank, Risk-Adjusted Bidding', '22', '2.2'],
          ['Cost Control (EVM)', 'Snapshot Engine, KPIs, S-Curve, EAC, Portfolio Heatmap, Baseline, Data Quality', '21', '2.1'],
          ['Project Execution', 'Milestone-Based Progress + RA Trigger, Mobile DPR, Risk Register', '21', '2.1'],
          ['Procurement', '3-Way Matching, Vendor Scoring + AVL, Multi-Location Inventory, Enhanced MRP', '24', '2.4'],
          ['Subcontractor', 'SC Master + PQ, Work Order, SC RA Bills, Retention Tracker', '26', '2.6'],
          ['QHSE', 'NCR + RCA, Incident + TRIR/LTIFR, Permit-to-Work, Toolbox Talk', '25', '2.5'],
          ['Equipment', 'Equipment Master, Hire In/Out, PM Scheduler, Fuel & Lubricant', '22', '2.2'],
          ['Doc / Engineering', 'Drawing Approval, Submittal Register, RFI Tracker', '16', '1.6'],
          ['Analytics', 'Executive Daily Digest', '4', '0.4'],
          ['Platform / DevOps', 'HA basics — replica set, automated backups, monitoring, DR plan', '10', '1.0'],
          ['PM + QA + Buffer (15%)', 'Testing, regression, UAT support', '—', '5.0'],
          ['Phase 1 Total', '', '362', '41.2'],
        ]}
        highlightLastRow
        align={[null, null, 'right', 'right']}
      />
      <Para><i>Phase 1 range: <b>₹38L – ₹52L</b> depending on scope tightness, biometric hardware selection, and external QA needs.</i></Para>

      <H3>Phase 2 — Operational Maturity (Months 4–9)</H3>
      <DataTable
        headers={['Workstream', 'Scope', 'Effort (days)', '₹ Lakh']}
        rows={[
          ['BD Depth', 'Key Personnel CV Bank, JV/Consortium, Client 360°, Award Kit Handoff', '21', '2.1'],
          ['HR Depth', 'LMS, Engagement + Retention, PMS Compensation Loop', '23', '2.3'],
          ['Bid Depth', 'Bid/No-Bid Decision Matrix, Post-Bid Analytics', '9', '0.9'],
          ['Project Exec Depth', 'Snag Mobile, Method Statements + JSA, As-Built Register, P6/MSP', '27', '2.7'],
          ['Procurement Depth', 'e-Auction, Material Master Substitutes', '16', '1.6'],
          ['Subcontractor Depth', 'SC PMC Scorecard, Back-charge Management', '9', '0.9'],
          ['QHSE Depth', 'Safety KPI Dashboard, Environmental Compliance', '10', '1.0'],
          ['Equipment Depth', 'Utilization Analytics, Calibration Register', '7', '0.7'],
          ['Doc / Engineering Depth', 'CAD/BIM Version Control, Transmittal Log, As-Built Compilation', '18', '1.8'],
          ['Analytics', 'Data Warehouse, BI Layer, Mobile Executive App', '38', '3.8'],
          ['WhatsApp Integration', 'Outbound notifications + Inbound NLP tasks', '20', '2.0'],
          ['Platform', 'Mobile apps, DR site, multi-env CI/CD, SSO', '30', '3.0'],
          ['PM + QA + Buffer (15%)', '', '—', '5.0'],
          ['Phase 2 Total', '', '228', '27.8'],
        ]}
        highlightLastRow
        align={[null, null, 'right', 'right']}
      />
      <Para><i>Phase 2 range: <b>₹55L – ₹75L</b> (mobile apps + WhatsApp BSP fees + SSO licensing are variable).</i></Para>

      <H3>Phase 3 — Scale to ₹5,000 Cr (Months 10–24)</H3>
      <DataTable
        headers={['Workstream', 'Scope', 'Effort (days)', '₹ Lakh']}
        rows={[
          ['BIM / 4D Integration', 'Revit/Tekla model viewer, 4D progress overlay', '12', '1.2'],
          ['Predictive ML', 'Cost-overrun prediction, claim auto-gen, vendor anomaly detection', '20', '2.0'],
          ['Equipment Analytics', 'Hire vs Buy TCO', '4', '0.4'],
          ['Multi-Entity / Multi-Currency', 'SPVs, subsidiaries, JV books with consolidation; USD/AED if international', '30', '3.0'],
          ['External Integrations', 'Tally/SAP, Primavera P6, BIM (Revit/Tekla), GSP, banking APIs', '25', '2.5'],
          ['Microservices Migration', 'Split monolith into Auth, Finance, HR, Projects services with Kafka', '60', '6.0'],
          ['ISO 27001 + SOC 2', 'Audit prep + remediation + certification fees (₹15L)', '20', '2.0'],
          ['Row-Level Security', 'Granular access at row level (PMs see only their projects)', '8', '0.8'],
          ['Compliance Add-Ons', 'DPDP Act, vendor portal, e-procurement portal', '20', '2.0'],
          ['Innovation R&D', 'Drone integration, IoT sensors at sites, AI document IQ on contracts', '30', '3.0'],
          ['PM + QA + Buffer (20%)', '', '—', '9.0'],
          ['Phase 3 Total', '', '229', '31.9 + ₹15L certs'],
        ]}
        highlightLastRow
        align={[null, null, 'right', 'right']}
      />
      <Para><i>Phase 3 range: <b>₹70L – ₹95L</b> (including ISO + SOC 2 certification fees of ₹12-18L).</i></Para>
    </section>
  );
}

function CostSummary() {
  return (
    <section id="cost-summary">
      <SectionHeader
        overline="15 · Cost Summary"
        title="Consolidated 24-month investment & ongoing run-rate"
        lead="All numbers in Indian Rupees (₹). Engineering effort costed at a blended ₹10,000 per engineer-day (Architect ₹15K, QA ₹6K, DevOps ₹12K, Senior Engineer ₹10K — blended midpoint), reflecting FY26 Indian market."
      />

      <H3>24-Month Build (Capex)</H3>
      <DataTable
        headers={['Category', 'Phase 1', 'Phase 2', 'Phase 3', 'Total (₹ Lakh)']}
        rows={[
          ['Engineering effort (build)', '41.2', '27.8', '31.9', '100.9'],
          ['Architecture / DevOps Capex', '1.0', '3.0', '—', '4.0'],
          ['3rd-party integrations (BGV, biometric, WhatsApp BSP, Tally)', '—', '5.0', '5.0', '10.0'],
          ['ISO 27001 + SOC 2 certification (one-time)', '—', '—', '15.0', '15.0'],
          ['Tooling / SaaS licenses (Datadog, Cloudflare, Metabase) — 2-yr', '5.0', '10.0', '15.0', '30.0'],
          ['Contingency (10%)', '5.0', '5.0', '7.0', '17.0'],
          ['TOTAL', '52.2', '50.8', '73.9', '176.9'],
        ]}
        highlightLastRow
        align={[null, 'right', 'right', 'right', 'right']}
      />

      <Callout kind="info" title="Headline Ask">
        <b>₹1.6 – ₹2.2 Crore over 24 months.</b> Range driven by hardware (biometric devices, mobile devices for site engineers), 3rd-party SaaS license scaling, and ISO/SOC2 certification fees.
      </Callout>

      <H3>Annual Run-Rate (Opex post-investment)</H3>
      <DataTable
        headers={['Recurring Cost Item', 'FY26', 'FY27', 'FY28+']}
        rows={[
          ['Cloud infrastructure (compute, storage, CDN)', '6', '12', '20'],
          ['SaaS licenses (Datadog, Cloudflare, Sentry, Metabase, Slack, GitHub)', '8', '15', '22'],
          ['3rd-party APIs (BGV, WhatsApp BSP, Email, SMS, GSP, banking)', '5', '12', '20'],
          ['Maintenance + bug-fix engineering (10-15% of build cost)', '5', '10', '15'],
          ['ISO/SOC 2 annual surveillance audits', '—', '3', '5'],
          ['Penetration testing (annual)', '3', '3', '4'],
          ['Total annual run-rate (₹ Lakh / year)', '27', '55', '86'],
        ]}
        highlightLastRow
        align={[null, 'right', 'right', 'right']}
      />

      <H3>3-Year Total Cost of Ownership</H3>
      <DataTable
        headers={['Line Item', '₹ Lakh']}
        rows={[
          ['Build (Capex) — 24 months', '176.9'],
          ['Recurring (FY26 + FY27 + FY28 partial-year) — opex', '82.0'],
          ['Total 3-year TCO', '258.9'],
        ]}
        highlightLastRow
        align={[null, 'right']}
      />

      <Callout kind="success" title="Cost Benchmarking">
        SAP S/4 HANA: ~₹7-14 Cr (license + implementation). Oracle Cloud ERP: ~₹2-4 Cr/yr SaaS. Microsoft Dynamics 365: ~₹1.5-3 Cr/yr SaaS. RIB Software / Procore: ₹3-6 Cr/yr. All require multi-year vendor lock-in. Ellora's in-house path: <b>~₹2.6 Cr 3-year TCO with full IP ownership and zero vendor lock-in.</b>
      </Callout>
    </section>
  );
}

function Risks() {
  return (
    <section id="risks">
      <SectionHeader
        overline="16 · Risks · Assumptions · Recommendations"
        title="What can go wrong, and what we recommend the board approve."
      />
      <H3>Key risks</H3>
      <DataTable
        headers={['#', 'Risk', 'Likelihood', 'Impact', 'Mitigation']}
        rows={[
          ['1', "Engineering team scaling — current 1-engineer pace can't deliver in 24 months", 'High', 'High', 'Hire 2 Sr engineers (month 1) + 1 architect (month 3). Outsource specific modules where bandwidth short.'],
          ['2', 'User adoption resistance from site engineers and procurement teams', 'Medium', 'Medium', 'Roll out site-by-site, not big-bang. Each site has a "super user" trained. New projects only initially.'],
          ['3', "Statutory compliance miss — single missed GST/TDS = ₹10-50K penalty + interest", 'High at FY26 without automation', 'Medium per occurrence', 'Phase 1 GST + statutory calendar non-negotiable. Backup manual filing during transition.'],
          ['4', 'Data migration from Tally + spreadsheets', 'High', 'Medium', 'Dedicated migration sprint (3-4 weeks) in Phase 1. Validation suite. 1-month parallel run.'],
          ['5', 'Security breach — at ₹5,000 Cr, Ellora becomes ransomware target', 'Medium', 'Catastrophic', 'Phase 1 monitoring + DR + backup mandatory. Phase 3 ISO 27001 + SOC 2 = audit-grade baseline.'],
          ['6', 'Scope creep — every department head will request features', 'High', 'Medium', 'Formal change-request board (MD + CTO + Functional Head). Quarterly backlog review. Firm phase boundaries.'],
        ]}
      />

      <H3>Recommendations</H3>
      <div className="space-y-4 max-w-3xl">
        {[
          ['1. APPROVE Phase 1 immediately', '₹38-52L for the next 3 months. Includes non-negotiable items (Finance, ATS, Payroll, EVM, BOCW) for operating ₹1,000 Cr safely.'],
          ['2. ALLOCATE a 4-engineer + 1-PM team', 'Current single-engineer pace is insufficient for 24-month roadmap.'],
          ['3. CONDITIONAL APPROVAL for Phase 2 & 3', 'Review after Phase 1 delivers, with quarterly board updates.'],
          ['4. APPOINT a CTO or VP Engineering', 'At ₹1,000 Cr scale, ERP becomes strategic; needs board-level sponsorship.'],
          ['5. EXECUTIVE STEERING COMMITTEE', 'MD + CFO + COO + HR Head + CTO to review monthly. Quarterly board update.'],
        ].map(([title, desc]) => (
          <div key={title} className="bg-emerald-50 border-l-4 border-emerald-500 rounded-r-sm p-5">
            <div className="font-bold text-emerald-900 text-[14px] mb-1">{title}</div>
            <p className="text-[13.5px] text-slate-700 leading-[1.7]">{desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ClosingCTA() {
  return (
    <section className="border-t border-slate-200 pt-10">
      <div className="bg-gradient-to-br from-[#1c2a4d] to-[#3b4872] text-white rounded-sm p-10 lg:p-14 max-w-3xl">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 mb-4">For Board Discussion</div>
        <h2 className="font-serif text-2xl md:text-3xl font-bold leading-tight mb-4" style={{ fontFamily: 'Georgia, serif' }}>
          The question isn't whether to invest. It's whether Ellora wants to be ₹5,000 Cr by 2030 — or stall at ₹1,500 Cr.
        </h2>
        <p className="text-white/80 leading-relaxed text-[14.5px] max-w-2xl">
          Every contractor in India that crossed ₹5,000 Cr did so with a mature ERP. The
          alternative is operational chaos that caps growth at ₹1,500–2,000 Cr — the
          well-documented &quot;mid-cap EPC wall&quot;.
        </p>
        <p className="text-white/80 leading-relaxed text-[14.5px] max-w-2xl mt-4">
          A 24-month, ₹1.6–2.2 Cr investment representing 0.16% of FY26 target revenue
          delivers ₹80–200 Cr of margin and working-capital unlock. ROI is conservatively
          50–125×. Board approval is requested for Phase 1.
        </p>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#1c2a4d] text-white/70 mt-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <img src="/assets/ellora-group-white.png" alt="Ellora" className="h-7 opacity-90" />
          <div className="text-[11px]">
            <div className="font-bold text-white">Ellora EPC Pvt Ltd</div>
            <div>ERP Strategy · Internal Distribution Only · v1.0</div>
          </div>
        </div>
        <div className="text-[11px]">
          © {new Date().getFullYear()} Ellora EPC Pvt Ltd. Confidential.
        </div>
      </div>
    </footer>
  );
}
